import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash/fp";

import type {
  ApiSearchResultMatch,
  ApiSearchResults,
  SearchCategory,
  SearchRequestParams,
  SearchResult,
  SearchResultMatch,
} from "@app/analytics/types";
import {
  getDirectionFromDelta,
  getQueryKeyFromDateRanges,
} from "@app/analytics/utils";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import type { User } from "@app/common/types";
import {
  optionalParseUTCDateTimeFromString,
  parseUTCDateTimeFromString,
  titleize,
} from "@app/utils/utils";

const getQueryKey = (query: Partial<SearchRequestParams>) => {
  const { startDate, endDate, ...params } = query;
  return JSON.stringify({
    ...params,
    dateRange:
      startDate && endDate
        ? getQueryKeyFromDateRanges([{ startDate, endDate }])
        : null,
  });
};

const getMatchLabel = (match: ApiSearchResultMatch) => {
  switch (match.category) {
    case "action_item":
    case "key_question":
    case "summary":
    case "topic":
    case "chapter":
    case "note":
      return titleize(match.category);
    case "visual_engagement": {
      // if we can't determine if it's low or high, treat as a standard transcript turn match
      if (!match.data.visualEngagementDelta) return;
      const direction = getDirectionFromDelta(match.data.visualEngagementDelta);
      return `Reaction - ${
        direction === "positive" ? "High" : "Low"
      } Engagement`;
    }
    case "visual_sentiment": {
      // if we can't determine if it's low or high, treat as a standard transcript turn match
      if (!match.data.visualSentimentDelta) return;
      const direction = getDirectionFromDelta(match.data.visualSentimentDelta);
      return `Reaction - ${
        direction === "positive" ? "High" : "Low"
      } Sentiment`;
    }
    default:
      // no label if title or turn
      return;
  }
};

const parseSearchResultMatches = (
  matches: ApiSearchResultMatch[]
): SearchResultMatch[] =>
  matches.map((match) => {
    const startTime = optionalParseUTCDateTimeFromString(match.data.startTime);
    const endTime = optionalParseUTCDateTimeFromString(match.data.endTime);
    const duration =
      startTime && endTime
        ? Math.max(endTime.diff(startTime).as("seconds"), 2)
        : undefined;

    return {
      id: match.data.id,
      category: match.category,
      duration,
      label: getMatchLabel(match),
      startTime,
      text: match.data.text,
    };
  });

const parseSearchResults = (
  results: ApiSearchResults,
  userId?: User["id"]
): SearchResult[] => {
  if (isEmpty(results?.results)) return [];

  return results.results.map((result) => ({
    startDate: parseUTCDateTimeFromString(result.startDate),
    matches: parseSearchResultMatches(result.matches),
    numMatches: result.numMatches,
    ownerIsCurrentUser: userId === result.ownerUserId,
    ownerName: result.ownerName,
    relevance: result.relevanceScore,
    sessionId: result.sessionId,
    summary: result.summary,
    title: result.title ?? "Untitled Meeting",
    thumbnailSrc: result.thumbnailUrl,
  }));
};

const filterSearchResultsByCategories = (
  categories: SearchCategory[],
  results: SearchResult[]
) => {
  if (isEmpty(categories)) return results;

  return results.filter((result) => {
    const matches = result.matches.filter((match) =>
      categories.includes(match.category)
    );
    return !isEmpty(matches);
  });
};

const EMPTY_RESULTS: SearchResult[] = [];
const EMPTY_CATEGORIES: SearchCategory[] = [];

const useSearchMeetingsQuery = (
  params: Partial<SearchRequestParams>,
  { enabled } = { enabled: true }
) => {
  const { user } = useAuth();

  // only enable query once the params include all the required fields
  const isSingleMeetingSearch = Boolean(params.sessionId);
  const isAllMeetingsSearch =
    Boolean(params.startDate) && Boolean(params.endDate);
  const queryIsEnabled =
    (isSingleMeetingSearch || isAllMeetingsSearch) && Boolean(params.query);

  const { categories, ...searchParams } = params;

  const searchQuery = useQuery({
    queryKey: ["sessionsList", "search", getQueryKey(searchParams)],
    queryFn: async () => {
      const searchResults = await apiClient.sessions.searchSessions(
        searchParams as SearchRequestParams
      );
      return parseSearchResults(searchResults, user?.id);
    },
    enabled: queryIsEnabled && enabled,
    staleTime: 30 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // The default MUST be the same object reference otherwise
  // the memoization will not work as expected since the default
  // will be a new object reference on every render
  const allResults = searchQuery.data ?? EMPTY_RESULTS;

  const results = useMemo(() => {
    return filterSearchResultsByCategories(
      categories ?? EMPTY_CATEGORIES,
      allResults
    );
  }, [categories, allResults]);

  return {
    ...searchQuery,
    results,
    allResults,
  };
};

export default useSearchMeetingsQuery;
